import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  TextField,
  Tooltip,
  Collapse,
  Paper,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as AcceptIcon,
  Edit as EditIcon,
  Cancel as RejectIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';

/**
 * AI Review Panel — shows AI-extracted field values with confidence scores.
 * User can accept, edit, or reject each suggestion individually.
 * Accepted suggestions are merged into the form values.
 */

export interface AIFieldSuggestion {
  fieldKey: string;
  fieldLabel: string;
  value: any;
  confidence: number;  // 0-1
  source?: string;     // excerpt from document
}

export interface AIReviewPanelProps {
  suggestions: AIFieldSuggestion[];
  documentName?: string;
  summary?: string;
  documentType?: string;
  warnings?: string[];
  onAccept: (fieldKey: string, value: any) => void;
  onAcceptAll: () => void;
  onDismiss: () => void;
}

type FieldDecision = 'pending' | 'accepted' | 'edited' | 'rejected';

const ConfidenceBar: React.FC<{ value: number }> = ({ value }) => {
  const color = value >= 0.8 ? 'success' : value >= 0.5 ? 'warning' : 'error';
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <LinearProgress
        variant="determinate"
        value={value * 100}
        color={color}
        sx={{ flex: 1, height: 4, borderRadius: 2 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ width: 30, textAlign: 'right' }}>
        {Math.round(value * 100)}%
      </Typography>
    </Box>
  );
};

const AIReviewPanel: React.FC<AIReviewPanelProps> = ({
  suggestions,
  documentName,
  summary,
  documentType,
  warnings = [],
  onAccept,
  onAcceptAll,
  onDismiss,
}) => {
  const [decisions, setDecisions] = useState<Record<string, FieldDecision>>(() =>
    Object.fromEntries(suggestions.map(s => [s.fieldKey, 'pending']))
  );
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [expandedSource, setExpandedSource] = useState<string | null>(null);

  const handleAccept = (s: AIFieldSuggestion) => {
    onAccept(s.fieldKey, editedValues[s.fieldKey] !== undefined ? editedValues[s.fieldKey] : s.value);
    setDecisions(d => ({ ...d, [s.fieldKey]: 'accepted' }));
  };

  const handleEdit = (s: AIFieldSuggestion) => {
    if (editedValues[s.fieldKey] === undefined) {
      setEditedValues(ev => ({ ...ev, [s.fieldKey]: typeof s.value === 'string' ? s.value : JSON.stringify(s.value) }));
    }
    setDecisions(d => ({ ...d, [s.fieldKey]: 'edited' }));
  };

  const handleReject = (key: string) => {
    setDecisions(d => ({ ...d, [key]: 'rejected' }));
  };

  const handleAcceptAll = () => {
    suggestions.forEach(s => {
      if (decisions[s.fieldKey] !== 'rejected') {
        onAccept(s.fieldKey, editedValues[s.fieldKey] !== undefined ? editedValues[s.fieldKey] : s.value);
      }
    });
    setDecisions(d => Object.fromEntries(Object.keys(d).map(k => [k, d[k] === 'rejected' ? 'rejected' : 'accepted'])));
    onAcceptAll();
  };

  const pendingCount = Object.values(decisions).filter(d => d === 'pending').length;
  const acceptedCount = Object.values(decisions).filter(d => d === 'accepted' || d === 'edited').length;

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
        <AIIcon color="primary" sx={{ mt: 0.25 }} />
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            AI Extraction Results
          </Typography>
          {documentName && (
            <Typography variant="caption" color="text.secondary">
              From: {documentName}{documentType ? ` (${documentType})` : ''}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onDismiss}>
          <RejectIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Summary */}
      {summary && (
        <Box mb={1.5} sx={{ p: 1, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
          <Typography variant="body2" color="info.dark">{summary}</Typography>
        </Box>
      )}

      {/* Warnings */}
      {warnings.map((w, i) => (
        <Box key={i} mb={0.5} sx={{ p: 0.75, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
          <Typography variant="caption" color="warning.dark">{w}</Typography>
        </Box>
      ))}

      <Divider sx={{ my: 1 }} />

      {/* Progress summary */}
      <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
        <Chip label={`${suggestions.length} fields found`} size="small" />
        {acceptedCount > 0 && <Chip label={`${acceptedCount} accepted`} size="small" color="success" />}
        {pendingCount > 0 && <Chip label={`${pendingCount} pending`} size="small" color="primary" variant="outlined" />}
      </Box>

      {/* Field suggestions */}
      <Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {suggestions.map(s => {
          const decision = decisions[s.fieldKey];
          const isEditing = decision === 'edited';
          const isAccepted = decision === 'accepted';
          const isRejected = decision === 'rejected';

          const displayValue = typeof s.value === 'object' ? JSON.stringify(s.value) : String(s.value || '');

          return (
            <Box
              key={s.fieldKey}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: isAccepted ? 'success.300' : isRejected ? 'grey.300' : 'grey.200',
                bgcolor: isAccepted ? 'success.50' : isRejected ? 'grey.100' : 'background.paper',
                opacity: isRejected ? 0.6 : 1,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {s.fieldLabel}
                </Typography>
                <Box display="flex" gap={0.5}>
                  {!isAccepted && !isRejected && (
                    <>
                      <Tooltip title="Accept this value">
                        <IconButton size="small" color="success" onClick={() => handleAccept(s)}>
                          <AcceptIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit before accepting">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject this suggestion">
                        <IconButton size="small" color="error" onClick={() => handleReject(s.fieldKey)}>
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {isAccepted && (
                    <Chip label="Accepted" size="small" color="success" icon={<AcceptIcon />} />
                  )}
                  {isRejected && (
                    <Button size="small" onClick={() => setDecisions(d => ({ ...d, [s.fieldKey]: 'pending' }))}>
                      Undo
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Value display / edit */}
              {isEditing ? (
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={editedValues[s.fieldKey] ?? displayValue}
                    onChange={e => setEditedValues(ev => ({ ...ev, [s.fieldKey]: e.target.value }))}
                    sx={{ mb: 0.5 }}
                  />
                  <Button size="small" variant="contained" onClick={() => handleAccept(s)}>
                    Accept edited
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: isRejected ? 'italic' : 'normal' }}>
                  {displayValue || <em>(empty)</em>}
                </Typography>
              )}

              {/* Confidence + source */}
              <Box mt={0.75}>
                <ConfidenceBar value={s.confidence} />
              </Box>
              {s.source && (
                <Box mt={0.5}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpandedSource(expandedSource === s.fieldKey ? null : s.fieldKey)}
                  >
                    <Typography variant="caption" color="text.secondary">Source excerpt</Typography>
                    {expandedSource === s.fieldKey ? <CollapseIcon sx={{ fontSize: 12 }} /> : <ExpandIcon sx={{ fontSize: 12 }} />}
                  </Box>
                  <Collapse in={expandedSource === s.fieldKey}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.25 }}>
                      "{s.source}"
                    </Typography>
                  </Collapse>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Actions */}
      {pendingCount > 0 && (
        <Box display="flex" gap={1} mt={1.5} justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={onDismiss}>Dismiss all</Button>
          <Button size="small" variant="contained" startIcon={<AcceptIcon />} onClick={handleAcceptAll}>
            Accept all remaining ({pendingCount})
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default AIReviewPanel;
